import * as VueRouter from 'vue-router'
import { generatePageTitle } from '@/utils/titleHelper';
import { useAccountStore } from '@/stores/account.js';

// Kept in step with URL_LOCALES in main.js. The router cannot import from
// main.js — main.js imports the router — so the list is repeated rather than
// shared.
const LOCALES = ['en', 'nl', 'de', 'es', 'fr', 'it', 'pl', 'pt'];

/**
 * Best guess at the language for a URL that carries none: what the visitor last
 * used, then what their browser asks for, then English.
 */
const guessLocale = () => {
    const stored = localStorage.getItem('lang');
    if (LOCALES.includes(stored)) {
        return stored;
    }

    const browser = new Intl.Locale(navigator.language || 'en').language;

    return LOCALES.includes(browser) ? browser : 'en';
};

const routes = [
    {
        path: '/',
        redirect: '/en',
    },
    {
        // LA-176: the URL that goes on QR codes, flyers and e-mail signatures.
        // Print carries no locale, so this forwards to the visitor's own.
        path: '/stay-updated',
        redirect: () => `/${guessLocale()}/stay-updated`,
    },
    {
        // LA-157: the unsubscribe link in an automated email. No locale, for
        // the same reason as the others: it is clicked out of an inbox.
        path: '/email-preferences/:token',
        redirect: (to) => `/${guessLocale()}/email-preferences/${to.params.token}`,
    },
    {
        // LA-194: the link an organiser gets over WhatsApp carries no locale —
        // it is meant to be one click from a chat message. Declared before
        // /:lang so "submit-event" is never mistaken for a language.
        path: '/submit-event/:code',
        redirect: (to) => `/${guessLocale()}/submit-event/${to.params.code}`,
    },
    {
        // LA-180: Brevo stores one confirmation redirect URL for the whole
        // list, so it cannot know which of the eight languages the subscriber
        // reads. This language-less entry point picks one and forwards.
        // Declared before /:lang so "newsletter" is never taken for a language.
        path: '/newsletter/confirmed',
        redirect: () => `/${guessLocale()}/newsletter/confirmed`,
    },
    {
        path: '/:lang',
        component: () => import(
            /* webpackChunkName: "layout" */
            '@/layouts/Default.vue'
        ),
        meta: {
            title: generatePageTitle(),
        },
        children: [
            {
                path: '',
                name: 'home',
                component: () => import(
                    /* webpackChunkName: "home" */
                    '@/pages/Home.vue'
                ),
                meta: {
                    title: generatePageTitle(),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The home page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The home page of Latinmap.'
                        }
                    ],
                },
            },
            {
                // LA-176: the fixed sign-up destination for every campaign.
                // Unlike the confirmation page this one is content, so it is in
                // the sitemap and the prerender route list.
                path: 'stay-updated',
                name: 'stay_updated',
                component: () => import(
                    /* webpackChunkName: "stay_updated" */
                    '@/pages/Newsletter/StayUpdated.vue'
                ),
                // The page is the sign-up form; the footer's own block would be
                // a second, competing one directly beneath it.
                meta: { hideFooterNewsletter: true },
            },
            {
                // LA-157: where someone stops the automated emails. Noindex and
                // out of the sitemap — the URL is personal to one address.
                path: 'email-preferences/:token',
                name: 'email_preferences',
                component: () => import(
                    /* webpackChunkName: "email_preferences" */
                    '@/pages/EmailPreferences.vue'
                ),
            },
            {
                // LA-194: submitting an event without an account. Not in the
                // sitemap or the prerender list, and noindex on the page
                // itself: indexing it would publish the code that protects it.
                path: 'submit-event/:code',
                name: 'submit_event',
                component: () => import(
                    /* webpackChunkName: "submit_event" */
                    '@/pages/SubmitEvent.vue'
                ),
            },
            {
                // Where Brevo sends someone after they confirm their newsletter
                // subscription (LA-180). Not in the sitemap or the prerender
                // route list on purpose — it is transactional, not content.
                path: 'newsletter/confirmed',
                name: 'newsletter_confirmed',
                component: () => import(
                    /* webpackChunkName: "newsletter_confirmed" */
                    '@/pages/Newsletter/Confirmed.vue'
                ),
            },
            {
                path: 'user/:key/blocked/:reason',
                name: 'blocked',
                component: () => import(
                    /* webpackChunkName: "home" */
                    '@/pages/Blocked.vue'
                ),
            },
            {
                path: 'searchresults/:query',
                name: 'searchresults',
                component: () => import(/* webpackChunkName: "results" */ '@/pages/Search/SearchResults.vue'),
                meta: {
                    title: generatePageTitle('Searchresult'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The result page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The result page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'events',
                name: 'events',
                component: () => import(
                    /* webpackChunkName: "events" */
                    '@/pages/Event/Events.vue'
                ),
                meta: {
                    title: generatePageTitle('Events'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The events page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The events page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'events/:filter_type/:filter+',
                name: 'events_prefiltered',
                component: () => import(
                    /* webpackChunkName: "events" */
                    '@/pages/Event/Events.vue'
                ),
                meta: {
                    title: generatePageTitle('Events'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The events page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The events page of Latinmap.'
                        }
                    ]
                },
                children: [
                    {
                        path: 'view/:view_type',
                        name: 'events_prefiltered_view',
                        component: () => import(
                            /* webpackChunkName: "events" */
                            '@/pages/Event/Events.vue'
                        ),
                        meta: {
                            title: generatePageTitle('Events'),
                            metaTags: [
                                {
                                    name: 'description',
                                    content: 'The events page of Latinmap.'
                                },
                                {
                                    property: 'og:description',
                                    content: 'The events page of Latinmap.'
                                }
                            ]
                        }
                    }
                ]
            },
            {
                path: 'events/user_filter/:search_id',
                name: 'user_stored_events_search',
                component: () => import(
                    /* webpackChunkName: "events" */
                    '@/pages/Event/Events.vue'
                ),
                meta: {
                    title: generatePageTitle('Events'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The events page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The events page of Latinmap.'
                        }
                    ]
                },
            },
            {
                path: 'event/:event',
                name: 'event_detail',
                component: () => import(
                    /* webpackChunkName: "events" */
                    '@/pages/Event/EventDetail.vue'
                ),
                meta: {
                    title: generatePageTitle(),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The event page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The event page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'organizations/:organization_type',
                name: 'organizations',
                component: () => import(
                    /* webpackChunkName: "artists" */
                    '@/pages/Organization/Organizations.vue'
                ),
                meta: {
                    title: generatePageTitle('Artists'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The artists page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The artists page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'organization/user_filter/:search_id',
                name: 'user_stored_organizations_search',
                component: () => import(
                    /* webpackChunkName: "organization" */
                    '@/pages/Organization/Organizations.vue'
                ),
                meta: {
                    title: generatePageTitle('Artists'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The Artists page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The Artists page of Latinmap.'
                        }
                    ]
                },
            },
            {
                path: 'organization/:organization',
                name: 'organization_detail',
                component: () => import(
                    /* webpackChunkName: "organizations" */
                    '@/pages/Organization/OrganizationDetail.vue'
                ),
                meta: {
                    title: generatePageTitle(),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The artists page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The artists page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'request-transfer/:organization',
                name: 'organization_request_transfer',
                component: () => import(
                    /* webpackChunkName: "organization_initiate_transfer" */
                    '@/pages/Organization/TransferRequest/Create.vue'
                ),
            },
            {
                path: 'dash',
                children: [
                    {
                        path: 'login',
                        name: 'account_login',
                        component: () => import(
                            /* webpackChunkName: "login" */
                            '@/pages/Onboarding/Login.vue'
                        ),
                        meta: {
                            title: generatePageTitle('Login'),
                            requiresGuest: true,
                            metaTags: [
                                {
                                    name: 'description',
                                    content: 'The login page of Latinmap.'
                                },
                                {
                                    property: 'og:description',
                                    content: 'The login page of Latinmap.'
                                }
                            ]
                        }
                    },
                    {
                        path: 'register/email/verify/:id/:hash',
                        name: 'account_register_verify',
                        component: () => import(
                            /* webpackChunkName: "account_register_verify" */
                            '@/pages/Onboarding/Login.vue'
                        ),
                    },
                    {
                        path: 'password-forgotten',
                        name: 'account_password_forgotten',
                        component: () => import(
                            /* webpackChunkName: "account_password_forgotten" */
                            '@/pages/Onboarding/PasswordForgotten.vue'
                        ),
                        meta: {
                            title: generatePageTitle('Password forgotten'),
                            metaTags: [
                                {
                                    name: 'description',
                                    content: 'The password forgotten page of Latinmap.'
                                },
                                {
                                    property: 'og:description',
                                    content: 'The password forgotten page of Latinmap.'
                                }
                            ]
                        }
                    },
                    {
                        path: 'password-reset/:hash',
                        name: 'account_password_reset',
                        component: () => import(
                            /* webpackChunkName: "account_password_reset" */
                            '@/pages/Onboarding/PasswordReset.vue'
                        ),
                    },
                    {
                        path: 'register',
                        name: 'account_register',
                        component: () => import(
                            /* webpackChunkName: "account_register" */
                            '@/pages/Onboarding/Register.vue'
                        ),
                        meta: {
                            title: generatePageTitle('Register'),
                            requiresGuest: true,
                            metaTags: [
                                {
                                    name: 'description',
                                    content: 'The register page of Latinmap.'
                                },
                                {
                                    property: 'og:description',
                                    content: 'The register page of Latinmap.'
                                }
                            ]
                        }
                    },
                    {
                        path: 'phone-verification',
                        name: 'phone_verification',
                        component: () => import(
                            /* webpackChunkName: "phone_verification" */
                            '@/pages/Account/PhoneVerification.vue'
                        ),
                        meta: { requiresAuth: true },
                    },
                    {
                        path: ':view_mode',
                        component: () => import(
                            /* webpackChunkName: "dashboard" */
                            '@/layouts/Account/Dashboard.vue'
                        ),
                        meta: { requiresAuth: true },
                        children: [
                            {
                                path: 'dashboard',
                                name: 'dashboard',
                                component: () => import(
                                    /* webpackChunkName: "dashboard" */
                                    '@/pages/Account/My/Dashboard.vue'
                                ),
                            },
                            {
                                path: 'profile',
                                name: 'profile',
                                component: () => import(
                                    /* webpackChunkName: "dashboard" */
                                    '@/pages/Account/My/Profile.vue'
                                ),
                            },
                            {
                                path: 'orders',
                                name: 'orders',
                                component: () => import(
                                    /* webpackChunkName: "orders" */
                                    '@/pages/Account/My/Orders.vue'
                                ),
                            },
                            {
                                path: 'dancers',
                                name: 'dancers',
                                component: () => import(
                                    /* webpackChunkName: "orders" */
                                    '@/pages/Account/My/Dancers.vue'
                                ),
                            },
                            {
                                path: 'latinmap',
                                name: 'latinmap',
                                component: () => import(
                                    /* webpackChunkName: "orders" */
                                    '@/pages/Account/My/Latinmap.vue'
                                ),
                            },
                            {
                                path: 'default_filters',
                                name: 'default_filters',
                                component: () => import(
                                    /* webpackChunkName: "dashboard" */
                                    '@/pages/Account/My/Filters.vue'
                                ),
                            },
                            {
                                path: 'favorites',
                                name: 'favorites',
                                component: () => import(
                                    /* webpackChunkName: "favorites" */
                                    '@/pages/Account/My/Favorites.vue'
                                ),
                            },
                            {
                                path: 'likes',
                                name: 'likes',
                                component: () => import(
                                    /* webpackChunkName: "likes" */
                                    '@/pages/Account/My/Likes.vue'
                                ),
                            },
                            {
                                path: 'leads',
                                name: 'leads',
                                // LA-200: hidden behind the flag rather than removed.
                                // Gating the route and not only the menu entry
                                // matters — this URL has been in the dashboard long
                                // enough to be bookmarked, and a page we have taken
                                // down should not still answer to whoever kept the
                                // link.
                                meta: { betaFeature: 'feature_member_leads' },
                                component: () => import(
                                    /* webpackChunkName: "favorites" */
                                    '@/pages/Account/My/Leads.vue'
                                ),
                            },
                            {
                                path: 'organizations',
                                children: [
                                    {
                                        path: '',
                                        name: 'managable_organization_overview',
                                        component: () => import(
                                            /* webpackChunkName: "organization_create" */
                                            '@/pages/Account/Organization/Dashboard.vue'
                                        ),
                                    },
                                    {
                                        path: 'create',
                                        name: 'organization_create',
                                        component: () => import(
                                            /* webpackChunkName: "organization_create" */
                                            '@/pages/Account/Organization/UpsertOrganization.vue'
                                        ),
                                    },
                                    {
                                        path: 'edit/:organization',
                                        name: 'organization_edit',
                                        component: () => import(
                                            /* webpackChunkName: "organization_edit" */
                                            '@/pages/Account/Organization/UpsertOrganization.vue'
                                        ),
                                        props: {
                                            mode: 'edit',
                                        }
                                    },
                                    {
                                        path: 'transfer/:organization',
                                        name: 'organization_initiate_transfer',
                                        component: () => import(
                                            /* webpackChunkName: "organization_initiate_transfer" */
                                            '@/pages/Account/Organization/Transfer/Create.vue'
                                        ),
                                    },
                                    {
                                        path: 'transfer',
                                        name: 'organization_transfer_overview',
                                        component: () => import(
                                            /* webpackChunkName: "organization_transfer_overview" */
                                            '@/pages/Account/Organization/Transfer/Overview.vue'
                                        ),
                                    },
                                    {
                                        path: 'authorized_people',
                                        name: 'organization_authorized_people_overview',
                                        component: () => import(
                                            /* webpackChunkName: "organization_transfer_overview" */
                                            '@/pages/Account/Organization/AuthorizedPeople/Overview.vue'
                                        ),
                                    },
                                    {
                                        path: ':organization/authorized_people',
                                        name: 'organization_authorized_people_manage',
                                        component: () => import(
                                            /* webpackChunkName: "organization_transfer_overview" */
                                            '@/pages/Account/Organization/AuthorizedPeople/Manage.vue'
                                        ),
                                    },
                                    {
                                        path: 'authorized_people/create',
                                        name: 'organization_authorized_people_create',
                                        component: () => import(
                                            /* webpackChunkName: "organization_create" */
                                            '@/pages/Account/Organization/AuthorizedPeople/UpsertAuthorizedPerson.vue'
                                        ),
                                    },
                                    {
                                        path: ':organization/authorized_people/:user_id/edit',
                                        name: 'organization_authorized_people_edit',
                                        component: () => import(
                                            /* webpackChunkName: "organization_edit" */
                                            '@/pages/Account/Organization/AuthorizedPeople/UpsertAuthorizedPerson.vue'
                                        ),
                                        props: {
                                            mode: 'edit',
                                        }
                                    },
                                    {
                                        path: 'transactions',
                                        name: 'transactions_account',
                                        component: () => import(
                                            /* webpackChunkName: "transactions_account" */
                                            '@/pages/Account/My/Transactions.vue'
                                        ),
                                    }
                                ]
                            },
                            {
                                path: 'account',
                                name: 'organization_account',
                                component: () => import(
                                    /* webpackChunkName: "organization_account" */
                                    '@/pages/Account/Organization/Account.vue'
                                ),
                            },
                            {
                                path: 'events',
                                children: [
                                    {
                                        path: 'overview',
                                        name: 'event_overview',
                                        component: () => import(
                                            /* webpackChunkName: "event_overview" */
                                            '@/pages/Account/Event/Overview.vue'
                                        ),
                                    },
                                    {
                                        path: 'create',
                                        name: 'event_create',
                                        component: () => import(
                                            /* webpackChunkName: "event_create" */
                                            '@/pages/Account/Event/UpsertEvent.vue'
                                        ),
                                    },
                                    {
                                        path: 'edit/:event',
                                        name: 'event_edit',
                                        component: () => import(
                                            /* webpackChunkName: "event_edit" */
                                            '@/pages/Account/Event/UpsertEvent.vue'
                                        ),
                                        props: {
                                            mode: 'edit',
                                        }
                                    },
                                    {
                                        path: 'manage_tickets/:event',
                                        meta: { betaFeature: 'feature_tickets_btn' },
                                        children: [
                                            {
                                                path: 'tickettypes',
                                                name: 'event_manage_tickettypes',
                                                component: () => import(
                                                    /* webpackChunkName: "event_manage_tickettypes" */
                                                    '@/pages/Account/Event/TicketType/Overview.vue'
                                                ),
                                            },
                                            {
                                                path: 'create_tickettype',
                                                name: 'event_create_tickettype',
                                                component: () => import(
                                                    /* webpackChunkName: "event_create_tickettype" */
                                                    '@/pages/Account/Event/TicketType/UpsetTicketType.vue'
                                                ),
                                            },
                                            {
                                                path: 'edit_tickettype/:ticketType',
                                                name: 'event_edit_tickettype',
                                                component: () => import(
                                                    /* webpackChunkName: "event_edit_tickettype" */
                                                    '@/pages/Account/Event/TicketType/UpsetTicketType.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'participants/:event',
                                        name: 'event_participants',
                                        meta: { betaFeature: 'feature_invite_participants' },
                                        component: () => import(
                                            /* webpackChunkName: "event_participants" */
                                            '@/pages/Account/Event/Participants/Overview.vue'
                                        ),
                                    }
                                ]
                            },
                            {
                                path: 'financial_center',
                                meta: { betaFeature: 'feature_financial_centre' },
                                children: [
                                    {
                                        path: '',
                                        name: 'dashboard_financial_center',
                                        component: () => import(
                                            /* webpackChunkName: "dashboard_financial_center" */
                                            '@/pages/FinancialDashboard/Dashboard.vue'
                                        ),
                                    },
                                ]
                            },
                            {
                                path: 'my_events',
                                children: [
                                    {
                                        path: '',
                                        name: 'my_events_overview',
                                        component: () => import(
                                            /* webpackChunkName: "my_events_overview" */
                                            '@/pages/Account/Event/Overview.vue'
                                        ),
                                    },
                                ]
                            },
                            {
                                path: '',
                                name: 'admin',
                                meta: { requiresAuth: true },
                                children: [
                                    {
                                        path: 'reports',
                                        children: [
                                            {
                                                path: 'overview/organizations',
                                                name: 'reports_overview_organizations',
                                                props: {
                                                    mode: 'organizations',
                                                },
                                                component: () => import(
                                                    /* webpackChunkName: "reports_overview" */
                                                    '@/pages/Admin/Reports/Overview.vue'
                                                ),
                                            },
                                            {
                                                path: 'overview/events',
                                                name: 'reports_overview_events',
                                                props: {
                                                    mode: 'events',
                                                },
                                                component: () => import(
                                                    /* webpackChunkName: "reports_overview" */
                                                    '@/pages/Admin/Reports/Overview.vue'
                                                ),
                                            },
                                            {
                                                path: ':archived/:type/:id',
                                                name: 'reports_by_type',
                                                component: () => import(
                                                    /* webpackChunkName: "reports_overview" */
                                                    '@/pages/Admin/Reports/OverviewByType.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'transfer',
                                        children: [
                                            {
                                                path: ':organization',
                                                name: 'admin_organization_initiate_transfer',
                                                component: () => import(
                                                    /* webpackChunkName: "admin_organization_initiate_transfer" */
                                                    '@/pages/Admin/Transfer/Create.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'block_users',
                                        children: [
                                            {
                                                path: '',
                                                name: 'block_users',
                                                component: () => import(
                                                    /* webpackChunkName: "block_users" */
                                                    '@/pages/Admin/BlockUsers/Overview.vue'
                                                ),
                                            },
                                            {
                                                path: 'create',
                                                name: 'block_users_create',
                                                component: () => import(
                                                    /* webpackChunkName: "block_users" */
                                                    '@/pages/Admin/BlockUsers/Upsert.vue'
                                                ),
                                            },
                                            {
                                                path: 'edit/:id',
                                                name: 'block_users_edit',
                                                component: () => import(
                                                    /* webpackChunkName: "block_users" */
                                                    '@/pages/Admin/BlockUsers/Upsert.vue'
                                                ),
                                            }
                                        ]
                                    },
                                    {
                                        path: 'objections',
                                        name: 'objections',
                                        component: () => import(
                                            /* webpackChunkName: "block_users" */
                                            '@/pages/Admin/Objections/Overview.vue'
                                        ),
                                    },
                                    {
                                        path: 'carousel-image',
                                        children: [
                                            {
                                                path: 'overview',
                                                name: 'admin_carousel_image_overview',
                                                component: () => import(
                                                    /* webpackChunkName: "admin_carousel_image_overview" */
                                                    '@/pages/Admin/CarouselImages/Overview.vue'
                                                ),
                                            },
                                            {
                                                path: 'image/:dance_style_id?',
                                                name: 'admin_upsert_carousel_image',
                                                component: () => import(
                                                    /* webpackChunkName: "carousel_image_upsert" */
                                                    '@/pages/Admin/CarouselImages/UpsertCarouselImage.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'beta-features',
                                        children: [
                                            {
                                                path: 'overview',
                                                name: 'admin_beta_feature_overview',
                                                component: () => import(
                                                    /* webpackChunkName: "admin_beta_feature_overview" */
                                                    '@/pages/Admin/BetaFeatures/Overview.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'settings',
                                        name: 'settings',
                                        component: () => import(
                                            /* webpackChunkName: "settings" */
                                            '@/pages/Admin/Settings/Overview.vue'
                                        ),
                                    },
                                    {
                                        path: 'leads',
                                        children: [
                                            {
                                                path: 'event_lead',
                                                name: 'leads_event',
                                                props: {
                                                    mode: 'event',
                                                },
                                                component: () => import(
                                                    /* webpackChunkName: "leads_event" */
                                                    '@/pages/Admin/Leads/Overview.vue'
                                                ),
                                            },
                                            {
                                                path: 'organization_lead',
                                                name: 'leads_organization',
                                                props: {
                                                    mode: 'organization',
                                                },
                                                component: () => import(
                                                    /* webpackChunkName: "organization" */
                                                    '@/pages/Admin/Leads/Overview.vue'
                                                ),
                                            },
                                            {
                                                path: ':id',
                                                name: 'leads_show',
                                                component: () => import(
                                                    /* webpackChunkName: "leads_overview" */
                                                    '@/pages/Admin/Leads/Show.vue'
                                                ),
                                            }
                                        ]
                                    },
                                    {
                                        path: 'permissions',
                                        children: [
                                            {
                                                path: 'overview',
                                                name: 'permissions_overview',
                                                component: () => import(
                                                    /* webpackChunkName: "permissions_overview" */
                                                    '@/pages/Admin/Permissions/Overview.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'invite_managers',
                                        children: [
                                            {
                                                path: 'overview',
                                                name: 'invite_overview',
                                                component: () => import(
                                                    /* webpackChunkName: "invite_overview" */
                                                    '@/pages/Admin/Invite/Overview.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'duplications',
                                        children: [
                                            {
                                                path: 'overview',
                                                name: 'duplications_overview',
                                                component: () => import(
                                                    /* webpackChunkName: "duplications_overview" */
                                                    '@/pages/Admin/Duplications/Overview.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'offical_brands',
                                        children: [
                                            {
                                                path: 'overview',
                                                name: 'offical_brands_overview',
                                                component: () => import(
                                                    /* webpackChunkName: "offical_brands_overview" */
                                                    '@/pages/Admin/OfficialBrands/Overview.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'export',
                                        name: 'organization_export_overview',
                                        component: () => import(
                                            /* webpackChunkName: "organization_export_overview" */
                                            '@/pages/Admin/Export/Overview.vue'
                                        ),
                                    },
                                    {
                                        path: 'edit_users',
                                        children: [
                                            {
                                                path: 'overview',
                                                name: 'edit_user_overview',
                                                component: () => import(
                                                    /* webpackChunkName: "edit_user_overview" */
                                                    '@/pages/Admin/EditUsers/Overview.vue'
                                                ),
                                            },
                                            {
                                                path: 'user/:user_id',
                                                name: 'admin_edit_user',
                                                component: () => import(
                                                    /* webpackChunkName: "admin_edit_user" */
                                                    '@/pages/Admin/EditUsers/Edit.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'edit_event_types',
                                        children: [
                                            {
                                                path: 'overview',
                                                name: 'edit_event_type_overview',
                                                component: () => import(
                                                    /* webpackChunkName: "edit_event_type_overview" */
                                                    '@/pages/Admin/EditEventTypes/Overview.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'edit_brand_types',
                                        children: [
                                            {
                                                path: 'overview',
                                                name: 'edit_brand_type_overview',
                                                component: () => import(
                                                    /* webpackChunkName: "edit_brand_type_overview" */
                                                    '@/pages/Admin/EditBrandTypes/Overview.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'edit_organizations',
                                        children: [
                                            {
                                                path: 'overview',
                                                name: 'edit_organizations_overview',
                                                component: () => import(
                                                    /* webpackChunkName: "edit_events_overview" */
                                                    '@/pages/Admin/EditOrganizations/Overview.vue'
                                                ),
                                            },
                                            {
                                                path: 'organization/:organization',
                                                name: 'admin_edit_organization',
                                                component: () => import(
                                                    /* webpackChunkName: "admin_edit_organization" */
                                                    '@/pages/Admin/EditOrganizations/Edit.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'edit_events',
                                        children: [
                                            {
                                                path: 'overview',
                                                name: 'edit_events_overview',
                                                component: () => import(
                                                    /* webpackChunkName: "edit_events_overview" */
                                                    '@/pages/Admin/EditEvents/Overview.vue'
                                                ),
                                            },
                                            {
                                                path: 'event/:event',
                                                name: 'admin_edit_event',
                                                component: () => import(
                                                    /* webpackChunkName: "admin_edit_event" */
                                                    '@/pages/Admin/EditEvents/Edit.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        // LA-194: making and withdrawing the
                                        // links organisers submit events with.
                                        path: 'submission_links',
                                        children: [
                                            {
                                                path: 'overview',
                                                name: 'submission_links_overview',
                                                component: () => import(
                                                    /* webpackChunkName: "submission_links_overview" */
                                                    '@/pages/Admin/SubmissionLinks/Overview.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'scraped_concepts',
                                        children: [
                                            {
                                                path: 'overview',
                                                name: 'scraped_concepts_overview',
                                                component: () => import(
                                                    /* webpackChunkName: "scraped_concepts_overview" */
                                                    '@/pages/Admin/ScrapedConcepts/Overview.vue'
                                                ),
                                            },
                                            {
                                                path: ':id',
                                                name: 'scraped_concept_edit',
                                                component: () => import(
                                                    /* webpackChunkName: "scraped_concept_edit" */
                                                    '@/pages/Admin/ScrapedConcepts/Edit.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'edit_pages',
                                        children: [
                                            {
                                                path: 'overview',
                                                name: 'edit_pages_overview',
                                                component: () => import(
                                                    /* webpackChunkName: "edit_pages_overview" */
                                                    '@/pages/Admin/EditPages/Overview.vue'
                                                ),
                                            },
                                            {
                                                path: 'page/:page',
                                                name: 'admin_edit_page',
                                                component: () => import(
                                                    /* webpackChunkName: "admin_edit_page" */
                                                    '@/pages/Admin/EditPages/UpsertPage.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'edit_dance_styles',
                                        children: [
                                            {
                                                path: 'overview',
                                                name: 'edit_dance_styles_overview',
                                                component: () => import(
                                                    /* webpackChunkName: "edit_user_overview" */
                                                    '@/pages/Admin/EditDanceStyles/Overview.vue'
                                                ),
                                            },
                                            {
                                                path: 'dance_style/:dance_style_id?',
                                                name: 'admin_upsert_dance_style',
                                                component: () => import(
                                                    /* webpackChunkName: "admin_edit_user" */
                                                    '@/pages/Admin/EditDanceStyles/UpsertDanceStyle.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'edit_music_styles',
                                        children: [
                                            {
                                                path: 'overview',
                                                name: 'edit_music_styles_overview',
                                                component: () => import(
                                                    /* webpackChunkName: "edit_user_overview" */
                                                    '@/pages/Admin/EditMusicStyles/Overview.vue'
                                                ),
                                            },
                                            {
                                                path: 'music_style/:music_style_id?',
                                                name: 'admin_upsert_music_style',
                                                component: () => import(
                                                    /* webpackChunkName: "admin_edit_user" */
                                                    '@/pages/Admin/EditMusicStyles/UpsertMusicStyle.vue'
                                                ),
                                            },
                                        ]
                                    },
                                    {
                                        path: 'featuredEvent',
                                        children: [
                                            {
                                                path: 'buy/:slug',
                                                meta: { betaFeature: 'feature_featured_events_btn' },
                                                name: 'buy_featured_event',
                                                component: () => import(
                                                    /*webpackChucnkName:"buy_featured_event" */
                                                    '@/pages/Admin/FeaturedEvent/BuyFeatured.vue'
                                                )
                                            },
                                            {
                                                path: 'createPrice',
                                                name: 'set_price_featured_event',
                                                component: () => import(
                                                    /*webpackChucnkName:"set_price_featured_event" */
                                                    '@/pages/Admin/FeaturedEvent/UpsertGlobalPrice.vue'
                                                )
                                            },
                                            {
                                                path: 'setFeaturedEvent/:eventSlug?',
                                                name: 'set_featured_event',
                                                component: () => import(
                                                    /*webpackChucnkName:"set_featured_event" */
                                                    '@/pages/Admin/FeaturedEvent/SetFeaturedEvent.vue'
                                                )
                                            },
                                        ]
                                    }
                                ]
                            },
                        ]
                    },
                ]
            },
            {
                path: 'transferlink/:organization/accept',
                component: () => import(
                    /* webpackChunkName: "accept_transfer_link" */
                    '@/pages/Account/My/AcceptTransferlink.vue'
                )
            }
        ]
    },
    {
        path: '/:lang/ticketstore',
        meta: {
            title: generatePageTitle(),
        },
        component: () => import(/* webpackChunkName: "layout" */ '@/layouts/Default.vue'),
        children: [
            {
                path: 'event/:event',
                name: 'ticket_store',
                component: () => import(/* webpackChunkName: "ticketstore" */ '@/pages/TicketStore/TicketStore.vue'),
            },
            {
                path: 'complete',
                name: 'ticket_store_complete',
                component: () => import(/* webpackChunkName: "ticketstore" */ '@/pages/TicketStore/TicketStoreComplete.vue'),
            }
        ]
    },
    {
        path: '/:lang/checkout',
        meta: {
            title: generatePageTitle(),
        },
        component: () => import(/* webpackChunkName: "layout" */ '@/layouts/Default.vue'),
        children: [
            {
                path: 'complete',
                name: 'checkout_complete',
                component: () => import(/* webpackChunkName: "checkout" */ '@/pages/Featured/Completed.vue'),
            }
        ]
    },
    {
        path: '/:lang/pages',
        meta: {
            title: generatePageTitle(),
            metaTags: [
                {
                    name: 'description',
                    content: 'The pages of Latinmap.'
                },
                {
                    property: 'og:description',
                    content: 'The pages of Latinmap.'
                }
            ]
        },
        component: () => import(/* webpackChunkName: "layout" */ '@/layouts/Default.vue'),
        children: [
            {
                path: 'contact',
                name: 'contact',
                component: () => import(/* webpackChunkName: "info" */ '@/pages/Info.vue'),
                props: {
                    page_slug: 'contact',
                },
                meta: {
                    requiresAuth: true,
                    title: generatePageTitle('Contact'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The contact page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The contact page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'terms-and-conditions',
                name: 'terms-and-conditions',
                component: () => import(/* webpackChunkName: "info" */ '@/pages/Info.vue'),
                props: {
                    page_slug: 'terms-and-conditions',
                },
                meta: {
                    title: generatePageTitle('Terms'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The terms page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The terms page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'privacy-policy',
                name: 'privacy-policy',
                component: () => import(/* webpackChunkName: "info" */ '@/pages/Info.vue'),
                props: {
                    page_slug: 'privacy-policy',
                },
                meta: {
                    title: generatePageTitle('Privacy'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The privacy page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The privacy page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'about-us',
                name: 'about-us',
                component: () => import(/* webpackChunkName: "info" */ '@/pages/Info.vue'),
                props: {
                    page_slug: 'about-us',
                },
                meta: {
                    title: generatePageTitle('About Latinmap'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The about page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The about page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'goods-and-services',
                name: 'goods-and-services',
                component: () => import(/* webpackChunkName: "info" */ '@/pages/Info.vue'),
                props: {
                    page_slug: 'goods-and-services',
                },
                meta: {
                    title: generatePageTitle('Goods and Services'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The goods and services page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The goods and services page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'how-it-works',
                name: 'how-it-works',
                component: () => import(/* webpackChunkName: "info" */ '@/pages/Info.vue'),
                props: {
                    page_slug: 'how-it-works',
                },
                meta: {
                    title: generatePageTitle('How it Works'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The how it works page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The how it works page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'help-center-faq',
                name: 'help-center-faq',
                component: () => import(/* webpackChunkName: "info" */ '@/pages/Info.vue'),
                props: {
                    page_slug: 'help-center-faq',
                },
                meta: {
                    title: generatePageTitle('Helpcentrum & FAQ'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The help center page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The help center page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'cookie-policy',
                name: 'cookie-policy',
                component: () => import(/* webpackChunkName: "info" */ '@/pages/Info.vue'),
                props: {
                    page_slug: 'cookie-policy',
                },
                meta: {
                    title: generatePageTitle('Cookie Policy'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The cookie policy page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The cookie policy page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'create-an-event',
                name: 'create-an-event',
                component: () => import(/* webpackChunkName: "Professionals" */ '@/pages/Professionals.vue'),
                props: {
                    page_slug: 'create-an-event',
                },
                meta: {
                    title: generatePageTitle('Create an event'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The create an event page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The create an event page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'create-an-brand-or-an-organization',
                name: 'create-an-brand-or-an-organization',
                component: () => import(/* webpackChunkName: "Professionals" */ '@/pages/Professionals.vue'),
                props: {
                    page_slug: 'create-an-brand-or-an-organization',
                },
                meta: {
                    title: generatePageTitle('Create an Brand or an Organization'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The create an brand or an organization page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The create an brand or an organization page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'become-an-ambassador',
                name: 'become-an-ambassador',
                component: () => import(/* webpackChunkName: "Professionals" */ '@/pages/Professionals.vue'),
                props: {
                    page_slug: 'become-an-ambassador',
                },
                meta: {
                    title: generatePageTitle('Become an Ambassador'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The become an ambassador page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The become an ambassador page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'promote-your-event',
                name: 'promote-your-event',
                component: () => import(/* webpackChunkName: "Professionals" */ '@/pages/Professionals.vue'),
                props: {
                    page_slug: 'promote-your-event',
                },
                meta: {
                    title: generatePageTitle('Promote your event'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The promote your event page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The promote your event page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'professional-account-verification',
                name: 'professional-account-verification',
                component: () => import(/* webpackChunkName: "Professionals" */ '@/pages/Professionals.vue'),
                props: {
                    page_slug: 'professional-account-verification',
                },
                meta: {
                    title: generatePageTitle('Professional Account Verification'),
                    metaTags: [
                        {
                            name: 'description',
                            content: 'The Professional Account Verification page of Latinmap.'
                        },
                        {
                            property: 'og:description',
                            content: 'The Professional Account Verification page of Latinmap.'
                        }
                    ]
                }
            },
            {
                path: 'about/:page_slug',
                name: 'about',
                component: () => import(/* webpackChunkName: "about" */ '@/pages/About.vue'),
            },
            {
                path: 'cta',
                name: 'cta',
                component: () => import(/* webpackChunkName: "about" */ '@/pages/Cta.vue'),
            },

        ]
    },
]

const router = VueRouter.createRouter({
    history: VueRouter.createWebHistory(),
    routes,
    linkExactActiveClass: 'active',
    scrollBehavior(to, from, savedPosition) {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                resolve({ left: 0, top: 0 })
            }, 10)
        })
    },
})

// handleLanguageMetaTags used to live here. It ran on every navigation and
// removed every link[rel=canonical] and link[rel=alternate] from the document,
// then, for non-English routes only, inserted a single canonical pointing at
// the English URL.
//
// That told Google not to index any non-English page, while the sitemap was
// advertising all eight languages with hreflang - the two contradicted each
// other. It also wiped the hreflang set that seoHelper and the build-time
// prerender emit, leaving 1 of 9 alternates on the page.
//
// Each page component already declares its own canonical and hreflang links
// through head(), so nothing needs to manage them imperatively.

const handleAuth = (to) => {
    const isAuthenticated = localStorage.getItem('token')
    const lang = to.params.lang || 'en'

    if (to.matched.some(record => record.meta.requiresAuth)) {
        if (!isAuthenticated) {
            return {
                name: 'account_login',
                params: { lang }
            }
        }
    }

    if (to.matched.some(record => record.meta.requiresGuest)) {
        if (isAuthenticated) {
            return {
                name: 'home',
                params: { lang }
            }
        }
    }

    return null
}


router.beforeEach((to, from, next) => {
    const authRedirect = handleAuth(to)
    if (authRedirect) {
        next(authRedirect);
        return;
    }

    next();
})

router.beforeEach((to, from, next) => {
    const accountStore = useAccountStore()
    const betaFeature = to.meta.betaFeature

    if (betaFeature && !accountStore.hasAccessToFeature(betaFeature)) {
        next({ name: 'home', params: { lang: to.params.lang } })
    } else {
        next()
    }
})

export default router
